import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api-client";

export interface CurrentBatchData {
  batch: {
    id: string;
    name: string;
    /** WU-7 — structured human-readable id (`<short>_<YYYYMMDD>_<seq>`).
     * Null for legacy rows that pre-date the backfill. */
    displayId: string | null;
    /** R-167 WU-7 — operator-rendered frozen label, null without a template. */
    customDisplayLabel: string | null;
    status: string;
    totalDocs: number;
    completedDocs: number;
  } | null;
  lockedDocs: Array<{
    id: string;
    filename: string;
    currentStep: string;
    batchId: string;
  }>;
  batchSessionExpiresAt: string | null;
}

const EMPTY: CurrentBatchData = {
  batch: null,
  lockedDocs: [],
  batchSessionExpiresAt: null,
};

/**
 * Polls `GET /api/queues/current-batch` once on mount and exposes a `refetch`
 * for callers that need to re-read after a side effect (e.g. batch check-in).
 *
 * Errors are swallowed back to the `EMPTY` shape — the banner is
 * decorative, so a transient 5xx should unmount it, not surface as a toast.
 */
export function useCurrentBatch() {
  const [data, setData] = useState<CurrentBatchData>(EMPTY);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<CurrentBatchData>("/queues/current-batch"));
    } catch {
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<CurrentBatchData>("/queues/current-batch")
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData(EMPTY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, refetch };
}
